import { useAtom } from 'jotai'
import { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router'
import mangoLogo from '~/assets/mango-logo-gradient.png'
import { FontStyle, Text } from '~/components/core'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import {
  usePartnerDesktopAppDownloadUrl,
  usePartnerDesktopAppName,
} from '~/hooks/useCurrentPartner'
import { toDesktopAppModeState } from '~/pages/Home/Banners/utils/desktopAppDetectionBannerLogic'
import {
  desktopAppDetectionBannerStateAtom,
  DesktopAppMode,
  getDesktopAppMode,
} from '~/pages/Home/Banners/utils/desktopAppDetectionBannerLogicState'
import {
  ContentWrapper,
  LoginButtonWrapper,
  LogoImage,
} from '~/pages/Login/Login.styles'
import { LoginBackground } from '~/pages/Login/LoginBackground'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'
import {
  HANDOFF_TARGET_PARAM,
  markDesktopHandoffHandled,
} from '~/utils/desktopAppHandoff'
import {
  buildDeepLinkForPath,
  openDesktopApp,
  toSafeInAppPath,
} from '~/utils/desktopDeepLink'
import { openInNewTab } from '~/utils/functions'

import {
  ActionStack,
  CopyStack,
  FallbackStack,
  settingsLinkStyle,
  wrappingCopyStyle,
} from './OpenApp.styles'

const DESKTOP_APP_SETTINGS_PATH = `${ApplicationRoutes.SETTINGS}/profile#desktop-app`

/**
 * Interstitial left in the tab after "Always open in desktop app" handed the
 * requested route (`?to=`) off. The launch itself happens before the router
 * mounts (see `redirectToDesktopHandoffIfNeeded`), so this page never
 * auto-launches and reloading it is safe. Browser-only: inside the desktop
 * app it redirects straight to the target.
 */
export const OpenedInDesktopPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const appName = usePartnerDesktopAppName()
  const downloadUrl = usePartnerDesktopAppDownloadUrl()
  const [desktopAppState, setDesktopAppState] = useAtom(
    desktopAppDetectionBannerStateAtom,
  )
  const isAlwaysMode =
    getDesktopAppMode(desktopAppState) === DesktopAppMode.Always

  const target = useMemo(
    () =>
      toSafeInAppPath(searchParams.get(HANDOFF_TARGET_PARAM) ?? '') ??
      ApplicationRoutes.HOME,
    [searchParams],
  )

  const handleTryAgain = useCallback(() => {
    openDesktopApp(buildDeepLinkForPath(target))
  }, [target])

  const handleContinueInBrowser = useCallback(() => {
    markDesktopHandoffHandled()
    navigate(target, { replace: true })
  }, [navigate, target])

  const handleStopOpeningInDesktop = useCallback(() => {
    setDesktopAppState(toDesktopAppModeState(DesktopAppMode.Ask))
    handleContinueInBrowser()
  }, [setDesktopAppState, handleContinueInBrowser])

  if (isElectron) return <Navigate to={target} replace />

  return (
    <LoginBackground>
      <ContentWrapper>
        <LogoImage alt="logo" src={mangoLogo} />
        <CopyStack>
          <Text
            fontStyle={FontStyle.Medium}
            align="center"
            text={t(
              'openedInDesktop.title',
              'We tried to open this page in the desktop app',
            )}
          />
          <Text
            fontStyle={FontStyle.Small}
            align="center"
            color="var(--chakra-colors-fg-muted)"
            style={wrappingCopyStyle}
            text={t('openedInDesktop.subtitle', {
              appName,
              defaultValue:
                'If the {{appName}} desktop app opened, you can close this tab.',
            })}
          />
        </CopyStack>
        <LoginButtonWrapper>
          <ActionStack>
            <MangoButton onClick={handleContinueInBrowser} size={Size.md}>
              {t('openedInDesktop.continueInBrowser', 'Continue in browser')}
            </MangoButton>
            <MangoButton
              variant={MangoButtonVariant.Brand_Text}
              onClick={handleTryAgain}
              size={Size.md}
            >
              {t('openedInDesktop.tryAgain', 'Try again')}
            </MangoButton>
            {isAlwaysMode ? (
              <MangoButton
                variant={MangoButtonVariant.Brand_Text}
                onClick={handleStopOpeningInDesktop}
                size={Size.sm}
              >
                {t(
                  'openedInDesktop.stopOpeningInDesktop',
                  'Stop opening links in the desktop app',
                )}
              </MangoButton>
            ) : null}
          </ActionStack>
        </LoginButtonWrapper>
        <FallbackStack>
          <Text
            fontStyle={FontStyle.Small}
            align="center"
            color="var(--chakra-colors-fg-muted)"
            text={t(
              'openApp.fallbackPrompt',
              "Don't have the desktop app installed?",
            )}
          />
          <MangoButton
            variant={MangoButtonVariant.Brand_Text}
            size={Size.sm}
            onClick={() => openInNewTab(downloadUrl)}
          >
            {t('openApp.downloadButton', 'Download the desktop app')}
          </MangoButton>
          <Text
            fontStyle={FontStyle.Small}
            align="center"
            color="var(--chakra-colors-fg-muted)"
            style={wrappingCopyStyle}
          >
            <Trans
              i18nKey="openedInDesktop.settingsHint"
              defaults="To keep links in this browser, change <settingsLink>Desktop App in Settings</settingsLink>."
              components={{
                settingsLink: (
                  <Link
                    to={DESKTOP_APP_SETTINGS_PATH}
                    onClick={markDesktopHandoffHandled}
                    style={settingsLinkStyle}
                  />
                ),
              }}
            />
          </Text>
        </FallbackStack>
      </ContentWrapper>
    </LoginBackground>
  )
}
